// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import Keycloak from 'keycloak-js';
import { get, writable } from 'svelte/store';
import { getConfig, getKeycloakConfig, isAutoLoginEnabled, isKeycloakEnabled } from '../config.js';

// Create Svelte stores for authentication state
export const isAuthenticated = writable(false);
export const userInfo = writable(null);
export const token = writable(null);
export const keycloakInitialized = writable(false);
export const keycloakIsDisabled = writable(!isKeycloakEnabled());

export const autoLoginEnabled = writable(isAutoLoginEnabled());

// Keycloak instance
let keycloak;

const config = getConfig();

/**
 * Initialize Keycloak instance and authentication state
 * @returns {Promise<boolean>} True if initialized successfully
 */
export const initKeycloak = async () => {
  // make sure that keycloak is initialized only once
  if (keycloak) {
    return get(isAuthenticated);
  }

  try {
    const keycloakConfig = getKeycloakConfig();

    keycloak = new Keycloak({
      url: keycloakConfig.url,
      realm: keycloakConfig.realm,
      clientId: keycloakConfig.clientId,
    });
    setupTokenRefresh();
    const onLoad = isAutoLoginEnabled() ? 'login-required' : 'check-sso';
    const authenticated = await keycloak.init({ onLoad });

    isAuthenticated.set(authenticated);
    keycloakInitialized.set(true);

    if (authenticated) {
      token.set(keycloak.token);

      userInfo.set({
        username: keycloak.idTokenParsed.preferred_username,
        name: keycloak.idTokenParsed.name,
        email: keycloak.idTokenParsed.email,
        roles: keycloak?.tokenParsed?.roles,
      });
    }

    return authenticated;
  } catch (error) {
    console.error('Failed to initialize Keycloak', error);
    isAuthenticated.set(false);
    keycloakInitialized.set(true);
    return false;
  }
};

/**
 * Set up automatic token refresh
 */
const setupTokenRefresh = () => {
  // Update token in store when it refreshes
  keycloak.onTokenExpired = () => {
    console.log('Token expired, refreshing...');
    keycloak
      .updateToken(30)
      .then((refreshed) => {
        if (refreshed) {
          console.log('Token refreshed');
          token.set(keycloak.token);
        } else {
          console.log(
            'Token not refreshed, valid for ' +
              Math.round(keycloak.tokenParsed.exp + keycloak.timeSkew - new Date().getTime() / 1000) +
              ' seconds',
          );
        }
      })
      .catch(() => {
        console.error('Failed to refresh token');
      });
  };
};

/**
 * Login with Keycloak
 */
export const login = () => {
  if (!keycloak) {
    console.error("Keycloak is not initialized. Make sure 'initKeycloak' has been successfully executed.");
    return;
  }

  keycloak.login({ idpHint: config.IDP_HINT });
};

/**
 * Logout from Keycloak
 */
export const logout = () => {
  keycloak.logout({ idpHint: config.IDP_HINT });
  if (config.KEYCLOAK_LOGOUT_PATH) {
    window.location.replace(config.KEYCLOAK_LOGOUT_PATH);
  }
};

/**
 * Get the current access token
 * @returns {string|null} The access token or null if not authenticated
 */
export const getToken = () => {
  return keycloak?.token || null;
};

/**
 * Check if the user has the specified role
 * @param {string} role The role to check
 * @returns {boolean} True if the user has the role
 */
export const hasRole = (role) => {
  const roles = keycloak?.tokenParsed?.roles || [];
  return roles.indexOf(role) !== -1;
};

/**
 * Update the token if it's about to expire
 * @returns {Promise<boolean>} True if the token was refreshed
 */
export const updateToken = async (minValidity = 30) => {
  try {
    const refreshed = await keycloak.updateToken(minValidity);
    if (refreshed) {
      token.set(keycloak.token);
    }
    return refreshed;
  } catch (error) {
    console.error('Failed to refresh token', error);
    return false;
  }
};

/**
 * Get the authentication headers for API requests
 * @returns {Object} The headers object with Authorization header
 */
export const getAuthHeaders = () => {
  const currentToken = getToken();
  return currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
};

export default {
  initKeycloak,
  login,
  logout,
  getToken,
  hasRole,
  updateToken,
  getAuthHeaders,
  isAuthenticated,
  userInfo,
  token,
  keycloakInitialized,
};
